import { FronteggContext } from '@frontegg/rest-api';
import {
  ActionMenuIcon,
  BinIcon,
  EditAppointmentIcon,
  PlusIcon,
} from 'src/icons';
import { axiosAiva } from 'src/lib/axios';
import { getFromStorage } from 'src/lib/storage';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dropdown, Form, Input, Menu, Modal, Switch, Tag } from 'antd';
import { Button } from '../../../baseComponents';
import { SuperAdminTable } from '../../../baseComponents/Table';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';
import OrganizationFilter from '../OrganizationFilter';
import { getAllActiveOrganizationAction } from 'src/modules/SuperAdmin/store/actions/organization.action';
import organizationEventEmitter from 'src/utilities/organizationEmmiter';

const parseApiError = (error, fallbackMessage) => {
  const apiMessage = error?.response?.data?.error;
  if (typeof apiMessage === 'string' && apiMessage.trim()) {
    return apiMessage;
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return fallbackMessage;
};

const getHeaders = () => {
  const token = FronteggContext.getAccessToken();
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
    'Headless-Mode': true,
  };

  const selectedOrganization = getFromStorage('superAdminSelectedOrganization');
  if (selectedOrganization) {
    try {
      const parsedOrganization = JSON.parse(selectedOrganization);
      if (parsedOrganization?.org_uuid) {
        headers['Impersonate-Org-UUID'] = `${parsedOrganization.org_uuid}`;
      }
    } catch (error) {
      // Ignore malformed local storage and fallback to default header set.
    }
  }

  return headers;
};

const SchedulingRulesPage = () => {
  const dispatch = useDispatch();
  const [rules, setRules] = useState([]);
  const [isListLoading, setIsListLoading] = useState(false);
  const [isRuleEditorOpen, setIsRuleEditorOpen] = useState(false);
  const [isRuleSubmitting, setIsRuleSubmitting] = useState(false);
  const [isOpeningEditor, setIsOpeningEditor] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [initialAttributes, setInitialAttributes] = useState([]);
  const [ruleForm] = Form.useForm();

  const getRules = useCallback(async () => {
    setIsListLoading(true);
    try {
      const result = await axiosAiva.get('/superadmin/scheduling-rule', {
        headers: getHeaders(),
      });
      const responseRules = Array.isArray(result?.data?.rules)
        ? result.data.rules
        : [];
      setRules(
        responseRules.map((rule) => ({
          ...rule,
          key: rule.rule_id,
        }))
      );
    } catch (error) {
      showToastError(
        parseApiError(error, 'Failed to fetch scheduling rules list')
      );
      setRules([]);
    } finally {
      setIsListLoading(false);
    }
  }, []);

  useEffect(() => {
    dispatch(getAllActiveOrganizationAction());
  }, [dispatch]);

  useEffect(() => {
    getRules();
  }, [getRules]);

  useEffect(() => {
    const unsubscribe = organizationEventEmitter.subscribe(
      'organizationChanged',
      () => {
        getRules();
      }
    );

    return () => unsubscribe();
  }, [getRules]);

  const closeRuleEditor = () => {
    setIsRuleEditorOpen(false);
    setEditingRuleId(null);
    setInitialAttributes([]);
    ruleForm.resetFields();
  };

  const openCreateRuleEditor = () => {
    setEditingRuleId(null);
    setInitialAttributes([]);
    ruleForm.setFieldsValue({
      name: '',
      instructions: '',
      is_active: true,
      attributes: [],
    });
    setIsRuleEditorOpen(true);
  };

  const openEditRuleEditor = async (ruleId) => {
    setIsOpeningEditor(true);
    try {
      const result = await axiosAiva.get(
        `/superadmin/scheduling-rule/${ruleId}`,
        {
          headers: getHeaders(),
        }
      );
      const selectedRule = result?.data?.rule;
      if (!selectedRule?.rule_id) {
        throw new Error('Scheduling rule not found');
      }

      const attributes = Array.isArray(selectedRule.attributes)
        ? selectedRule.attributes
        : [];
      setEditingRuleId(selectedRule.rule_id);
      setInitialAttributes(attributes);
      ruleForm.setFieldsValue({
        name: selectedRule.name || '',
        instructions: selectedRule.instructions || '',
        is_active: selectedRule.is_active !== false,
        attributes: attributes.map((attribute) => ({
          attribute_id: attribute.attribute_id,
          type: attribute.type || '',
          value: attribute.value || '',
        })),
      });
      setIsRuleEditorOpen(true);
    } catch (error) {
      showToastError(
        parseApiError(error, 'Failed to fetch scheduling rule details')
      );
    } finally {
      setIsOpeningEditor(false);
    }
  };

  const syncAttributes = async (ruleId, nextAttributes) => {
    const existingById = new Map(
      initialAttributes
        .filter((attribute) => attribute?.attribute_id)
        .map((attribute) => [attribute.attribute_id, attribute])
    );
    const retainedIds = new Set();

    for (const attribute of nextAttributes) {
      const payload = {
        type: attribute?.type?.trim(),
        value: attribute?.value?.trim(),
      };
      if (!payload.type || !payload.value) {
        continue;
      }

      if (attribute?.attribute_id) {
        retainedIds.add(attribute.attribute_id);
        const existing = existingById.get(attribute.attribute_id);
        if (
          existing?.type === payload.type &&
          existing?.value === payload.value
        ) {
          continue;
        }
        await axiosAiva.put(
          `/superadmin/scheduling-rule/${ruleId}/attribute/${attribute.attribute_id}`,
          payload,
          { headers: getHeaders() }
        );
      } else {
        await axiosAiva.post(
          `/superadmin/scheduling-rule/${ruleId}/attribute`,
          payload,
          { headers: getHeaders() }
        );
      }
    }

    for (const [attributeId] of existingById) {
      if (!retainedIds.has(attributeId)) {
        await axiosAiva.delete(
          `/superadmin/scheduling-rule/${ruleId}/attribute/${attributeId}`,
          { headers: getHeaders() }
        );
      }
    }
  };

  const submitRuleEditor = async () => {
    try {
      const values = await ruleForm.validateFields();
      setIsRuleSubmitting(true);

      const nextAttributes = (values?.attributes || []).map((attribute) => ({
        attribute_id: attribute?.attribute_id,
        type: attribute?.type?.trim(),
        value: attribute?.value?.trim(),
      }));

      const rulePayload = {
        name: values?.name?.trim(),
        instructions: values?.instructions ?? '',
        is_active: values?.is_active !== false,
      };

      if (!editingRuleId) {
        await axiosAiva.post(
          '/superadmin/scheduling-rule',
          {
            ...rulePayload,
            attributes: nextAttributes
              .filter((attribute) => attribute.type && attribute.value)
              .map(({ type, value }) => ({ type, value })),
          },
          { headers: getHeaders() }
        );
        showToastSuccess('Scheduling rule created successfully');
      } else {
        await axiosAiva.put(
          `/superadmin/scheduling-rule/${editingRuleId}`,
          rulePayload,
          { headers: getHeaders() }
        );
        await syncAttributes(editingRuleId, nextAttributes);
        showToastSuccess('Scheduling rule updated successfully');
      }

      closeRuleEditor();
      await getRules();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      showToastError(parseApiError(error, 'Failed to save scheduling rule'));
    } finally {
      setIsRuleSubmitting(false);
    }
  };

  const handleArchiveRule = (ruleId) => {
    Modal.confirm({
      title: 'Archive this scheduling rule?',
      content:
        'This performs a soft delete and hides the rule from active list views.',
      okText: 'Archive/Soft Delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await axiosAiva.delete(`/superadmin/scheduling-rule/${ruleId}`, {
            headers: getHeaders(),
          });
          showToastSuccess('Scheduling rule archived successfully');
          await getRules();
        } catch (error) {
          showToastError(
            parseApiError(error, 'Failed to archive scheduling rule')
          );
        }
      },
    });
  };

  const ruleActionsMenu = (rule) => (
    <Menu
      onClick={({ key }) => {
        if (key === 'edit') {
          openEditRuleEditor(rule.rule_id);
        }
        if (key === 'archive') {
          handleArchiveRule(rule.rule_id);
        }
      }}
    >
      <Menu.Item key="edit">
        <div className="dropdown-text flex items-center space-x-2">
          <EditAppointmentIcon /> <span>Edit rule</span>
        </div>
      </Menu.Item>
      <Menu.Item key="archive">
        <div className="dropdown-text flex items-center space-x-2">
          <BinIcon /> <span>Archive/Soft Delete</span>
        </div>
      </Menu.Item>
    </Menu>
  );

  const ruleColumns = [
    {
      title: <span className="font-[700]">Name</span>,
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a?.name || '').localeCompare(b?.name || ''),
    },
    {
      title: <span className="font-[700]">Attributes</span>,
      dataIndex: 'attributes',
      key: 'attributes',
      render: (_, record) => {
        const attributes = Array.isArray(record?.attributes)
          ? record.attributes
          : [];
        if (!attributes.length) {
          return 'N/A';
        }
        return (
          <div className="flex flex-wrap gap-1">
            {attributes.map((attribute) => (
              <Tag
                key={
                  attribute.attribute_id ||
                  `${attribute.type}:${attribute.value}`
                }
              >
                {attribute.type}: {attribute.value}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: <span className="font-[700]">Active</span>,
      dataIndex: 'is_active',
      key: 'is_active',
      render: (_, record) =>
        record?.is_active ? (
          <Tag color="success">Active</Tag>
        ) : (
          <Tag color="default">Inactive</Tag>
        ),
    },
    {
      title: <span className="font-[700]">Last modified by</span>,
      dataIndex: 'last_modified_by',
      key: 'last_modified_by',
      render: (_, record) => record?.last_modified_by || 'N/A',
    },
    {
      title: '',
      key: 'actions',
      dataIndex: 'actions',
      render: (_, record) => (
        <Dropdown overlay={ruleActionsMenu(record)}>
          <a onClick={(event) => event.preventDefault()}>
            <ActionMenuIcon />
          </a>
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-end">
        <div className="flex flex-col items-end gap-3 w-full sm:w-auto">
          <OrganizationFilter />
          <Button type={'button'} onClick={openCreateRuleEditor}>
            <PlusIcon className="mr-2" /> Add scheduling rule
          </Button>
        </div>
      </div>

      <div className="mt-5 ant-table rounded-xl shadow-xl">
        <SuperAdminTable
          className={'appointment-page-table'}
          data={rules}
          columns={ruleColumns}
          loading={isListLoading || isOpeningEditor}
        />
      </div>

      <Modal
        open={isRuleEditorOpen}
        onCancel={closeRuleEditor}
        title={editingRuleId ? 'Edit scheduling rule' : 'Add scheduling rule'}
        okText={editingRuleId ? 'Save' : 'Create'}
        onOk={submitRuleEditor}
        confirmLoading={isRuleSubmitting}
        width={860}
        destroyOnClose
      >
        <Form
          form={ruleForm}
          layout="vertical"
          initialValues={{ is_active: true }}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[
              { required: true, whitespace: true, message: 'Name is required' },
              { max: 255, message: 'Name must be 255 characters or fewer' },
            ]}
          >
            <Input placeholder="Enter rule name" maxLength={255} />
          </Form.Item>

          <Form.Item
            label="Instructions"
            name="instructions"
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'Instructions are required',
              },
            ]}
          >
            <Input.TextArea
              autoSize={{ minRows: 16, maxRows: 24 }}
              placeholder="Write instructions in markdown"
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          <Form.Item label="Active" name="is_active" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.List name="attributes">
            {(fields, { add, remove }) => (
              <div className="mt-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold">Attributes (key/value)</span>
                  <Button
                    type={'button'}
                    className={'text-sm py-1 px-3'}
                    onClick={() => add({ type: '', value: '' })}
                  >
                    <PlusIcon className="mr-2" /> Add key/value
                  </Button>
                </div>

                {fields.length === 0 && (
                  <div className="text-gray-500 text-sm mb-2">
                    No attributes added.
                  </div>
                )}

                <div className="flex flex-col gap-3">
                  {fields.map((field, index) => (
                    <div
                      key={field.key}
                      className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-start"
                    >
                      <Form.Item
                        name={[field.name, 'attribute_id']}
                        hidden
                        preserve
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        label={index === 0 ? 'Key (type)' : ''}
                        name={[field.name, 'type']}
                        rules={[
                          {
                            required: true,
                            whitespace: true,
                            message: 'Key is required',
                          },
                          {
                            max: 255,
                            message: 'Key must be 255 characters or fewer',
                          },
                        ]}
                      >
                        <Input placeholder="technologyName" maxLength={255} />
                      </Form.Item>

                      <Form.Item
                        label={index === 0 ? 'Value' : ''}
                        name={[field.name, 'value']}
                        rules={[
                          {
                            required: true,
                            whitespace: true,
                            message: 'Value is required',
                          },
                          {
                            max: 255,
                            message: 'Value must be 255 characters or fewer',
                          },
                        ]}
                      >
                        <Input placeholder="Ultrasound" maxLength={255} />
                      </Form.Item>

                      <div className="md:pt-8">
                        <Button
                          type={'cancel'}
                          className={'py-2 px-3'}
                          onClick={() => remove(field.name)}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

export default SchedulingRulesPage;
